import { fetchGetResponse } from "../../utilities/ApiUtilities";

export const FX_ASSET_TYPE = "FOREIGN_EXCHANGE";
export const DEFAULT_FX_TRADE_TYPE = "FX_SPOT";
export const FX_TRADE_TYPE_OPTIONS = [
  { value: "FX_SPOT", label: "Spot" },
  { value: "FX_FORWARD", label: "Forward" },
];

export const EMPTY_TICKER_UNIVERSE = {
  defaultSelection: null,
  options: {},
};

const normalizeAssetType = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

export const formatCoTraderFilterLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeOption = (option) => {
  if (!option || typeof option !== "object") {
    return null;
  }

  const value = String(option.value || "").trim();
  if (!value) {
    return null;
  }

  return {
    value,
    label: formatCoTraderFilterLabel(option.label || value),
    quote_symbol: String(option.quote_symbol || "").trim(),
  };
};

const normalizeFxOptions = (rawOptions) =>
  (Array.isArray(rawOptions) ? rawOptions : [])
    .map(normalizeOption)
    .filter(Boolean);

const normalizeScopedOptions = (rawOptions) => {
  if (!rawOptions || typeof rawOptions !== "object" || Array.isArray(rawOptions)) {
    return {};
  }

  return Object.entries(rawOptions).reduce((countryAccumulator, [country, categories]) => {
    if (!categories || typeof categories !== "object" || Array.isArray(categories)) {
      return countryAccumulator;
    }

    const normalizedCategories = Object.entries(categories).reduce(
      (categoryAccumulator, [category, symbols]) => {
        const normalizedSymbols = (Array.isArray(symbols) ? symbols : [])
          .map(normalizeOption)
          .filter(Boolean);

        if (normalizedSymbols.length > 0) {
          categoryAccumulator[country ? category : ""] = normalizedSymbols;
        }

        return categoryAccumulator;
      },
      {},
    );

    if (Object.keys(normalizedCategories).length > 0) {
      countryAccumulator[country] = normalizedCategories;
    }

    return countryAccumulator;
  }, {});
};

export const normalizeTickerUniverseOptions = (rawOptions) => {
  if (!rawOptions || typeof rawOptions !== "object" || Array.isArray(rawOptions)) {
    return {};
  }

  return Object.entries(rawOptions).reduce((optionsAccumulator, [assetType, assetOptions]) => {
    const normalizedAssetType = normalizeAssetType(assetType);
    if (!normalizedAssetType) {
      return optionsAccumulator;
    }

    if (normalizedAssetType === FX_ASSET_TYPE) {
      const normalizedFxOptions = normalizeFxOptions(assetOptions);
      if (normalizedFxOptions.length > 0) {
        optionsAccumulator[normalizedAssetType] = normalizedFxOptions;
      }
      return optionsAccumulator;
    }

    const normalizedScopedOptions = normalizeScopedOptions(assetOptions);
    if (Object.keys(normalizedScopedOptions).length > 0) {
      optionsAccumulator[normalizedAssetType] = normalizedScopedOptions;
    }

    return optionsAccumulator;
  }, {});
};

const normalizeDefaultSelection = (defaultSelection) => {
  if (!defaultSelection || typeof defaultSelection !== "object") {
    return null;
  }

  const assetType = normalizeAssetType(defaultSelection.asset_type);
  if (!assetType) {
    return null;
  }

  return {
    assetType,
    country: defaultSelection.country ? String(defaultSelection.country) : null,
    category: defaultSelection.category ? String(defaultSelection.category) : null,
    tradeType: DEFAULT_FX_TRADE_TYPE,
    value: defaultSelection.value ? String(defaultSelection.value) : "",
  };
};

const getUniverseErrorMessage = (status) => {
  if (status === 401) return "User is not authenticated.";
  return "Ticker universe is unavailable.";
};

export const fetchTickerUniverse = async ({ isDemoMode = false } = {}) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const response = isDemoMode
    ? await fetch(`${apiUrl}/demo/get_ticker_universe`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      })
    : await fetchGetResponse("get_ticker_universe");
  const payload = await response.json().catch(() => null);

  if (!response.ok || payload?.success === false) {
    const error = new Error(
      payload?.errorObject?.message ||
        payload?.message ||
        getUniverseErrorMessage(response.status),
    );
    error.status = response.status;
    throw error;
  }

  const results = payload?.results || {};

  return {
    defaultSelection: normalizeDefaultSelection(results.default_selection),
    options: normalizeTickerUniverseOptions(results.options),
  };
};

export const getAssetTypeLabel = (assetType) =>
  String(assetType || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const getAssetTypeOptions = (options = {}) =>
  Object.keys(options).map((assetType) => ({
    value: assetType,
    label: getAssetTypeLabel(assetType),
  }));

export const getCountryOptionsForAsset = (options = {}, assetType = "") =>
  Object.keys(options[assetType] || {}).map((country) => ({
    value: country,
    label: formatCoTraderFilterLabel(country),
  }));

export const getCategoryOptionsForAsset = (
  options = {},
  assetType = "",
  country = "",
) =>
  Object.keys(options[assetType]?.[country] || {}).map((category) => ({
    value: category,
    label: formatCoTraderFilterLabel(category),
  }));

export const getTickerOptionsForSelection = (
  options = {},
  assetType = "",
  country = "",
  category = "",
) => {
  if (assetType === FX_ASSET_TYPE) {
    return Array.isArray(options[assetType]) ? options[assetType] : [];
  }

  return Array.isArray(options[assetType]?.[country]?.[category])
    ? options[assetType][country][category]
    : [];
};

export const flattenAssetOptions = (options = {}, assetType = "") => {
  if (assetType === FX_ASSET_TYPE) {
    return getTickerOptionsForSelection(options, assetType).map((option) => ({
      ...option,
      assetType,
      country: null,
      category: null,
    }));
  }

  return Object.entries(options[assetType] || {}).flatMap(([country, categories]) =>
    Object.entries(categories || {}).flatMap(([category, scopedOptions]) =>
      (Array.isArray(scopedOptions) ? scopedOptions : []).map((option) => ({
        ...option,
        assetType,
        country,
        category,
      })),
    ),
  );
};

const getOptionByValue = (options = [], value = "") =>
  options.find((option) => option.value === String(value || "").trim()) || null;

export const resolveTickerUniverseSelection = ({
  options = {},
  defaultSelection = null,
  selection = {},
} = {}) => {
  const assetTypeOptions = getAssetTypeOptions(options);
  const fallbackAssetType = assetTypeOptions[0]?.value || "";
  const requestedAssetType = normalizeAssetType(
    selection.assetType || defaultSelection?.assetType,
  );
  const assetType = options[requestedAssetType]
    ? requestedAssetType
    : fallbackAssetType;

  if (!assetType) {
    return {
      assetType: "",
      country: null,
      category: null,
      tradeType: DEFAULT_FX_TRADE_TYPE,
      value: "",
      option: null,
    };
  }

  if (assetType === FX_ASSET_TYPE) {
    const tickerOptions = getTickerOptionsForSelection(options, assetType);
    const option =
      getOptionByValue(tickerOptions, selection.value) ||
      getOptionByValue(tickerOptions, defaultSelection?.value) ||
      tickerOptions[0] ||
      null;

    return {
      assetType,
      country: null,
      category: null,
      tradeType: String(selection.tradeType || DEFAULT_FX_TRADE_TYPE),
      value: option?.value || "",
      option,
    };
  }

  const requestedCountry = String(selection.country || defaultSelection?.country || "");
  const countryOptions = getCountryOptionsForAsset(options, assetType);
  const country = countryOptions.some((option) => option.value === requestedCountry)
    ? requestedCountry
    : countryOptions[0]?.value || null;

  const requestedCategory = String(
    selection.category || defaultSelection?.category || "",
  );
  const categoryOptions = getCategoryOptionsForAsset(options, assetType, country);
  const category = categoryOptions.some((option) => option.value === requestedCategory)
    ? requestedCategory
    : categoryOptions[0]?.value || null;

  const tickerOptions = getTickerOptionsForSelection(
    options,
    assetType,
    country,
    category,
  );
  const option =
    getOptionByValue(tickerOptions, selection.value) ||
    getOptionByValue(tickerOptions, defaultSelection?.value) ||
    tickerOptions[0] ||
    null;

  return {
    assetType,
    country,
    category,
    tradeType: DEFAULT_FX_TRADE_TYPE,
    value: option?.value || "",
    option,
  };
};

export const findSelectionForQuoteSymbol = (
  options = {},
  assetType = "",
  quoteSymbol = "",
) => {
  const normalizedQuoteSymbol = String(quoteSymbol || "").trim();
  if (!normalizedQuoteSymbol) {
    return null;
  }

  const matchingOption = flattenAssetOptions(options, assetType).find(
    (option) => option.quote_symbol === normalizedQuoteSymbol,
  );

  if (!matchingOption) {
    return null;
  }

  return {
    assetType,
    country: matchingOption.country,
    category: matchingOption.category,
    value: matchingOption.value,
  };
};

const normalizeTickerKey = (value = "") =>
  String(value || "")
    .toUpperCase()
    .replace(/^FX-/, "")
    .replace(/=X$/, "")
    .replace(/[^A-Z0-9.]/g, "");

// Finds the universe selection for a plain ticker such as "EURUSD" or "AAPL".
export const findSelectionForTicker = (options = {}, ticker = "") => {
  const wanted = normalizeTickerKey(ticker);
  if (!wanted) {
    return null;
  }

  for (const assetType of Object.keys(options)) {
    const match = flattenAssetOptions(options, assetType).find(
      (option) =>
        normalizeTickerKey(option.value) === wanted ||
        normalizeTickerKey(option.quote_symbol) === wanted,
    );
    if (match) {
      return {
        assetType,
        country: match.country,
        category: match.category,
        value: match.value,
      };
    }
  }

  return null;
};

export const getFxRuntimeSymbol = (value = "") =>
  String(value || "")
    .trim()
    .replace(/^FX-/i, "");
